/**
 * Account registry (Gordon 2026-07-20) — the GitHub-style "remembered accounts"
 * store behind the avatar-dropdown Account Switcher.
 *
 * SECURITY POSTURE (see also PR body): each remembered account keeps the SAME kind
 * of credential the app already keeps for its single live session — the DRF auth
 * token — in the SAME storage tier (localStorage). This introduces NO new security
 * regime: today one session token lives at localStorage["keyCloak"].token
 * (src/services/request.js); here up to MAX_ACCOUNTS such tokens live namespaced
 * under one key. The switch never weakens sensitive-action re-auth (2FA, password)
 * — those still run whatever the app does today. Tokens are never logged, never
 * rendered, and never sent anywhere except as the Authorization header the app
 * already uses.
 *
 * Isolation + wipe: entries are keyed by user id (one entry per account, no
 * cross-account merge). removeAccount() drops exactly one entry (wiping its token);
 * clearAll() removes the whole namespaced key ("sign out of all"). A signed-out
 * account leaves no residue in this store.
 *
 * All functions take an injectable `storage` (defaults to window.localStorage) and
 * never throw — a corrupt/blocked store degrades to "no remembered accounts".
 */

export const STORE_KEY = "iyuga.accounts.v1";
export const STORE_VERSION = 1;
/** Industry envelope (GitHub/Google): a small, bounded set of remembered accounts. */
export const MAX_ACCOUNTS = 5;

/** window.localStorage if present & usable, else a no-op store (never throws). */
export function defaultStorage() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch (_e) {
    /* access can throw in privacy mode */
  }
  return {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
}

function readStore(storage) {
  try {
    const raw = storage.getItem(STORE_KEY);
    if (!raw) return { version: STORE_VERSION, accounts: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.accounts)) {
      return { version: STORE_VERSION, accounts: [] };
    }
    return { version: STORE_VERSION, accounts: parsed.accounts };
  } catch (_e) {
    return { version: STORE_VERSION, accounts: [] };
  }
}

function writeStore(store, storage) {
  try {
    storage.setItem(
      STORE_KEY,
      JSON.stringify({ version: STORE_VERSION, accounts: store.accounts })
    );
  } catch (_e) {
    /* quota / blocked — remembering an account is best-effort, never fatal */
  }
}

/** Normalize an arbitrary entry to the stored shape; null if it has no usable id/token. */
function normalize(entry) {
  if (!entry || entry.id == null || !entry.token) return null;
  const id = String(entry.id);
  const email = entry.email ? String(entry.email) : "";
  const name = entry.name ? String(entry.name) : email || `User ${id}`;
  const avatar = entry.avatar ? String(entry.avatar) : "";
  return {
    id,
    email,
    name,
    avatar,
    token: String(entry.token),
    addedAt: entry.addedAt || Date.now(),
    lastActiveAt: entry.lastActiveAt || Date.now(),
  };
}

/**
 * Two different backend user ids can end up remembering the SAME human/email
 * (a re-issued id, an id carried over from before an account merge, etc.) —
 * fix-gordon-0928-P1: "dedupe the same account/email". One entry per id is not
 * enough; collapse same-email entries here (the single read path every
 * consumer uses) so the switcher never lists one person twice. Accounts with
 * no email on file (blank string) never collide with each other.
 */
function dedupeByEmail(accounts) {
  const byEmail = new Map();
  const result = [];
  for (const account of accounts) {
    const key = account.email ? account.email.toLowerCase() : null;
    if (key == null) {
      result.push(account);
      continue;
    }
    const kept = byEmail.get(key);
    if (!kept) {
      byEmail.set(key, account);
      result.push(account);
      continue;
    }
    // accounts are already sorted most-recently-active first, so `kept` (seen
    // earlier) is always at least as recent as `account` — nothing to do.
  }
  return result;
}

/** Remembered accounts, most-recently-active first. Never throws. */
export function getAccounts(storage = defaultStorage()) {
  const sorted = readStore(storage)
    .accounts.map(normalize)
    .filter(Boolean)
    .sort((a, b) => (b.lastActiveAt || 0) - (a.lastActiveAt || 0));
  return dedupeByEmail(sorted);
}

export function getAccount(id, storage = defaultStorage()) {
  if (id == null) return null;
  const target = String(id);
  return getAccounts(storage).find((a) => a.id === target) || null;
}

/**
 * Add or update one account (by id) and bump it to most-recent. Enforces MAX_ACCOUNTS
 * by evicting the least-recently-active entries. Returns the resulting list.
 */
export function upsertAccount(entry, storage = defaultStorage()) {
  const next = normalize(entry);
  if (!next) return getAccounts(storage);

  const existing = readStore(storage).accounts.map(normalize).filter(Boolean);
  const prior = existing.find((a) => a.id === next.id);
  const merged = {
    ...next,
    addedAt: prior ? prior.addedAt : next.addedAt,
    lastActiveAt: Date.now(),
  };
  const others = existing.filter((a) => a.id !== next.id);

  const accounts = [merged, ...others]
    .sort((a, b) => (b.lastActiveAt || 0) - (a.lastActiveAt || 0))
    .slice(0, MAX_ACCOUNTS);

  writeStore({ accounts }, storage);
  return accounts;
}

/** Forget exactly one account (wipes its stored token). Returns the resulting list. */
export function removeAccount(id, storage = defaultStorage()) {
  if (id == null) return getAccounts(storage);
  const target = String(id);
  const accounts = readStore(storage)
    .accounts.map(normalize)
    .filter(Boolean)
    .filter((a) => a.id !== target);
  writeStore({ accounts }, storage);
  return accounts;
}

/** Forget every remembered account ("sign out of all"). Removes the whole key. */
export function clearAll(storage = defaultStorage()) {
  try {
    storage.removeItem(STORE_KEY);
  } catch (_e) {
    /* ignore */
  }
}
